import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { convertTime, convertTimestampToDateTime } from "../../utils/ProductStatus";
import { useDistributor } from "../../hooks/useDistributor";
import { TablePagination } from "@mui/material";

function DistributorTransaction() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const distributor = useDistributor();

  useEffect(() => {
    if (!distributor || !distributor.uid) {
      console.warn("Distributor not found or UID is missing");
      return;
    }

    const fetchInventory = async () => {
      try {
        const inventoryCollection = collection(db, "transactions");
        const inventorySnapshot = await getDocs(inventoryCollection);
        const transactionList = await Promise.all(
          inventorySnapshot.docs.map(async (doc) => {
            let data = doc.data();

            if (!data.fromDistributorId || data.fromDistributorId.path !== `users/${distributor.uid}`) {
              return null;
            }

            const fromDistributorId = data.fromDistributorId?.path || "N/A";
            const toEntityId = data.toEntityId?.path || "N/A";

            if (data.fromDistributorId) {
              const fromDistributorDoc = await getDoc(data.fromDistributorId);
              if (fromDistributorDoc.exists()) {
                data.fromDistributor = fromDistributorDoc.data();
              }
            }

            if (data.toEntityId) {
              const toEntityDoc = await getDoc(data.toEntityId);
              if (toEntityDoc.exists()) {
                data.toEntity = toEntityDoc.data();
              }
            }

            return { id: doc.id, ...data, fromDistributorId, toEntityId };
          })
        );

        const filteredTransactionList = transactionList.filter(Boolean);

        filteredTransactionList.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));

        setTransactions(filteredTransactionList);
        setFilteredTransactions(filteredTransactionList);
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError(err.message);
      }
    };

    fetchInventory();
  }, [distributor]);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = transactions.filter((transaction) =>
      transaction.transactionNo?.toString().toLowerCase().includes(value) ||
      convertTime(transaction.date?.toDate()).toLowerCase().includes(value) ||
      transaction.bill?.toString().toLowerCase().includes(value) ||
      transaction.serialNumbers?.join(",").toLowerCase().includes(value) ||
      transaction.to?.toLowerCase().includes(value) ||
      transaction.toEntityId?.toLowerCase().includes(value)
    );

    setFilteredTransactions(filtered);
    setPage(0);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setFilteredTransactions(transactions);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center p-4 bg-white rounded-xl">
        <input
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearch}
          className="border border-red-600 p-2 rounded-lg text-lg"
        />
        <div className="flex">
          <button
            onClick={handleSearch}
            className="border border-red-600 bg-white text-black px-8 py-4 rounded-lg mx-2"
          >
            Search
          </button>
          <button
            onClick={clearSearch}
            className="border border-red-600 bg-white text-black px-8 py-4 rounded-lg mx-2"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border-collapse bg-white">
          <thead>
            <tr>
              <th className="py-3 px-4 bg-red-600 text-white text-sm font-semibold uppercase">Transaction No.</th>
              <th className="py-3 px-4 bg-red-600 text-white text-sm font-semibold uppercase">Date & Time</th>
              <th className="py-3 px-4 bg-red-600 text-white text-sm font-semibold uppercase">Bill Value</th>
              <th className="py-3 px-4 bg-red-600 text-white text-sm font-semibold uppercase">Quantity</th>
              <th className="py-3 px-4 bg-red-600 text-white text-sm font-semibold uppercase">Serial Numbers</th>
              <th className="py-3 px-4 bg-red-600 text-white text-sm font-semibold uppercase">Sold To</th>
              <th className="py-3 px-4 bg-red-600 text-white text-sm font-semibold uppercase">Sold To ID</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
              <tr key={index} className="hover:bg-gray-100 even:bg-gray-50">
                <td className="py-4 px-4 text-center text-black">{row.transactionNo}</td>
                <td className="py-4 px-4 text-center text-black">{convertTimestampToDateTime(row.date)}</td>
                <td className="py-4 px-4 text-center text-black">₹{row.bill}</td>
                <td className="py-4 px-4 text-center text-black">{row.serialNumbers?.length || 0}</td>
                <td className="py-4 px-4 text-center text-black">
                  {row.serialNumbers?.map((serial, serialIndex) => (
                    <React.Fragment key={serialIndex}>
                      <Link style={{ textDecoration: "none", color: "blue", marginRight: "5px" }}>{serial}</Link>
                      {serialIndex < row.serialNumbers.length - 1 && ", "}
                    </React.Fragment>
                  )) || "N/A"}
                </td>
                <td className="py-4 px-4 text-center text-black">{row.to?.toUpperCase() || "N/A"}</td>
                <td className="py-4 px-4 text-center text-black">
                  <Link to={`/distributor/retailers-details/${row.toEntity.uid}`} className="text-blue-600">
                    {row.toEntity.name.toUpperCase()}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TablePagination
        component="div"
        style={{ backgroundColor: "white" }}
        rowsPerPageOptions={[5, 10, 25]}
        count={filteredTransactions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}

export default DistributorTransaction;